import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { useMutation, useLazyQuery, gql } from '@apollo/client';

// Auth Events
export const AUTH_EVENTS = {
  LOGOUT: 'auth:logout',
  TOKEN_VALIDATED: 'auth:token_validated',
  TOKEN_INVALID: 'auth:token_invalid',
};

// Auth Event Dispatcher
export const dispatchAuthEvent = (eventType, data = null) => {
  const event = new CustomEvent(eventType, { detail: data });
  window.dispatchEvent(event);
};

// Function to retrieve cookie value
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const CREATE_CUSTOMER = gql`
  mutation createCustomer($input: CustomerInput!) {
    createCustomer(input: $input) {
      customer {
        id
        email
        firstname
        lastname
      }
    }
  }
`;

const GET_CUSTOMER = gql`
  query getCustomer {
    customer {
      id
      email
      firstname
      lastname
    }
  }
`;

const REVOKE_CUSTOMER_TOKEN = gql`
  mutation revokeCustomerToken {
    revokeCustomerToken {
      result
    }
  }
`;

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: !!action.payload };
    case 'SET_TOKEN':
      return { ...state, token: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_INITIAL_LOADING':
      return { ...state, initialLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOGIN_MODAL':
      return { ...state, isLoginModalOpen: action.payload };
    case 'SET_REGISTER_MODAL':
      return { ...state, isRegisterModalOpen: action.payload };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        initialLoading: false,
        isLoginModalOpen: false,
        isRegisterModalOpen: false
      };
    default:
      return state;
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    token: null,
    isAuthenticated: false,
    loading: false,
    initialLoading: true,
    error: null,
    isLoginModalOpen: false,
    isRegisterModalOpen: false
  });

  const [createCustomer] = useMutation(CREATE_CUSTOMER);
  const [revokeCustomerToken] = useMutation(REVOKE_CUSTOMER_TOKEN);
  const [getCustomer] = useLazyQuery(GET_CUSTOMER);

  // Validate token function
  const validateToken = async () => {
    try {
      const result = await getCustomer();
      if (result.data?.customer) {
        dispatch({ type: 'SET_USER', payload: result.data.customer });
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('customerToken');
        dispatch({ type: 'LOGOUT' });
      }
    } catch (error) {
      localStorage.removeItem('customerToken');
      dispatch({ type: 'LOGOUT' });
    } finally {
      // Initial auth check is complete
      dispatch({ type: 'SET_INITIAL_LOADING', payload: false });
    }
  };

  // Initialize user function - can be called on page load and after login
  const initUser = async () => {
    let token = localStorage.getItem('customerToken');

    // If no token in localStorage, try to get from cookie
    if (!token) {
      token = getCookie('customer_token');
      if (token) {
        // Save to localStorage for future use
        localStorage.setItem('customerToken', token);
      }
    }

    if (token) {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const result = await getCustomer();

        if (result.data?.customer) {
          dispatch({ type: 'SET_TOKEN', payload: token });
          dispatch({ type: 'SET_USER', payload: result.data.customer });
          dispatchAuthEvent(AUTH_EVENTS.TOKEN_VALIDATED, result.data.customer);
        } else {
          // Token is invalid, clear it
          localStorage.removeItem('customerToken');
          document.cookie = 'customer_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          dispatch({ type: 'LOGOUT' });
          dispatchAuthEvent(AUTH_EVENTS.TOKEN_INVALID);
        }
      } catch (error) {
        // Handle 403 or other errors - invalid token
        if (error.networkError?.statusCode === 403 || error.graphQLErrors?.some(e => e.extensions?.category === 'graphql-authorization')) {
          localStorage.removeItem('customerToken');
          document.cookie = 'customer_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          dispatch({ type: 'LOGOUT' });
          dispatchAuthEvent(AUTH_EVENTS.TOKEN_INVALID);
        }
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  };

  // Initialize auth on mount
  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    if (token) {
      dispatch({ type: 'SET_TOKEN', payload: token });
      validateToken();
    } else {
      // No token found, finish initial loading
      dispatch({ type: 'SET_INITIAL_LOADING', payload: false });
    }
  }, []);

  const login = async (email, password) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const response = await fetch('/customer/ajax/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          username: email,
          password: password
        }),
        credentials: 'include'
      });

      if (response.ok) {
        // Extract the customer_token cookie and save it in localStorage
        const customerToken = getCookie('customer_token');
        if (customerToken) {
          localStorage.setItem('customerToken', customerToken);
          dispatch({ type: 'SET_LOGIN_MODAL', payload: false });

          // Initialize user data after successful login
          await initUser();
          return { success: true };
        } else {
          throw new Error('Customer token not received');
        }
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const register = async (customerData) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const result = await createCustomer({
        variables: { input: customerData }
      });

      if (result.data?.createCustomer?.customer) {
        // Auto-login after successful registration
        const loginResult = await login(customerData.email, customerData.password);
        if (loginResult.success) {
          dispatch({ type: 'SET_REGISTER_MODAL', payload: false });
        }
        return { success: true };
      } else {
        throw new Error('Registration failed');
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async () => {
    try {
      await revokeCustomerToken();
    } catch (error) {
      console.error('GraphQL revoke token failed:', error);
    }

    try {
      // Then we send a GET request to logout
      await fetch('/customer/ajax/logout', {
        method: 'GET',
        credentials: 'include'
      });
    } catch (error) {
      console.error('REST logout request failed:', error);
    } finally {
      // Always clear local state regardless of query results
      localStorage.removeItem('customerToken');
      dispatch({ type: 'LOGOUT' });
      dispatchAuthEvent(AUTH_EVENTS.LOGOUT);
    }
  };

  const openLoginModal = () => {
    dispatch({ type: 'SET_LOGIN_MODAL', payload: true });
    dispatch({ type: 'SET_REGISTER_MODAL', payload: false });
  };

  const closeLoginModal = () => {
    dispatch({ type: 'SET_LOGIN_MODAL', payload: false });
  };

  const openRegisterModal = () => {
    dispatch({ type: 'SET_REGISTER_MODAL', payload: true });
    dispatch({ type: 'SET_LOGIN_MODAL', payload: false });
  };

  const closeRegisterModal = () => {
    dispatch({ type: 'SET_REGISTER_MODAL', payload: false });
  };

  const switchToRegister = () => {
    dispatch({ type: 'SET_LOGIN_MODAL', payload: false });
    dispatch({ type: 'SET_REGISTER_MODAL', payload: true });
  };

  const switchToLogin = () => {
    dispatch({ type: 'SET_REGISTER_MODAL', payload: false });
    dispatch({ type: 'SET_LOGIN_MODAL', payload: true });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    openLoginModal,
    closeLoginModal,
    openRegisterModal,
    closeRegisterModal,
    switchToRegister,
    switchToLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};